import React, { useEffect, useState, Component } from 'react';
import { ArrowRight, Star, Calendar, MapPin, Clock, ShieldCheck, Zap, Lock, Sun, Moon, CircleDot, ArrowUpRight, Download, FileText, Sparkles, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Accordion } from '../components/ui/Accordion';
import { PLANET_INTERPRETATIONS, ASCENDANT_INTERPRETATIONS } from '../data/natalInterpretations';
interface NatalChartDemoPageProps {
  onNavigate: (page: string) => void;
}
// Zodiac data
const ZODIAC_SIGNS = [{
  name: 'Овен',
  symbol: '♈',
  color: '#ef4444',
  element: 'Огонь'
}, {
  name: 'Телец',
  symbol: '♉',
  color: '#10b981',
  element: 'Земля'
}, {
  name: 'Близнецы',
  symbol: '♊',
  color: '#f59e0b',
  element: 'Воздух'
}, {
  name: 'Рак',
  symbol: '♋',
  color: '#94a3b8',
  element: 'Вода'
}, {
  name: 'Лев',
  symbol: '♌',
  color: '#f59e0b',
  element: 'Огонь'
}, {
  name: 'Дева',
  symbol: '♍',
  color: '#10b981',
  element: 'Земля'
}, {
  name: 'Весы',
  symbol: '♎',
  color: '#ec4899',
  element: 'Воздух'
}, {
  name: 'Скорпион',
  symbol: '♏',
  color: '#ef4444',
  element: 'Вода'
}, {
  name: 'Стрелец',
  symbol: '♐',
  color: '#8b5cf6',
  element: 'Огонь'
}, {
  name: 'Козерог',
  symbol: '♑',
  color: '#475569',
  element: 'Земля'
}, {
  name: 'Водолей',
  symbol: '♒',
  color: '#06b6d4',
  element: 'Воздух'
}, {
  name: 'Рыбы',
  symbol: '♓',
  color: '#14b8a6',
  element: 'Вода'
}];
const PLANETS = [{
  id: 'sun',
  name: 'Солнце',
  icon: <Sun className="w-4 h-4" data-id="element-21070" />,
  color: '#f59e0b'
}, {
  id: 'moon',
  name: 'Луна',
  icon: <Moon className="w-4 h-4" data-id="element-21071" />,
  color: '#94a3b8'
}, {
  id: 'mercury',
  name: 'Меркурий',
  icon: <CircleDot className="w-4 h-4" data-id="element-21072" />,
  color: '#10b981'
}, {
  id: 'venus',
  name: 'Венера',
  icon: <Star className="w-4 h-4" data-id="element-21073" />,
  color: '#ec4899'
}, {
  id: 'mars',
  name: 'Марс',
  icon: <ArrowUpRight className="w-4 h-4" data-id="element-21074" />,
  color: '#ef4444'
}, {
  id: 'jupiter',
  name: 'Юпитер',
  icon: <Star className="w-4 h-4" data-id="element-21075" />,
  color: '#8b5cf6'
}, {
  id: 'saturn',
  name: 'Сатурн',
  icon: <Star className="w-4 h-4" data-id="element-21076" />,
  color: '#475569'
}, {
  id: 'uranus',
  name: 'Уран',
  icon: <Star className="w-4 h-4" data-id="element-21077" />,
  color: '#06b6d4'
}, {
  id: 'neptune',
  name: 'Нептун',
  icon: <Star className="w-4 h-4" data-id="element-21078" />,
  color: '#3b82f6'
}, {
  id: 'pluto',
  name: 'Плутон',
  icon: <Star className="w-4 h-4" data-id="element-21079" />,
  color: '#8b5cf6'
}];
export function NatalChartDemoPage({
  onNavigate
}: NatalChartDemoPageProps) {
  const [showResults, setShowResults] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({
    name: 'Алексей',
    date: '1992-08-15',
    time: '14:30',
    city: 'Москва'
  });
  // Generated data based on input
  const [chartData, setChartData] = useState<any>(null);
  const getZodiacSign = (dateString: string) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    if (month === 3 && day >= 21 || month === 4 && day <= 19) return 0; // Aries
    if (month === 4 && day >= 20 || month === 5 && day <= 20) return 1; // Taurus
    if (month === 5 && day >= 21 || month === 6 && day <= 20) return 2; // Gemini
    if (month === 6 && day >= 21 || month === 7 && day <= 22) return 3; // Cancer
    if (month === 7 && day >= 23 || month === 8 && day <= 22) return 4; // Leo
    if (month === 8 && day >= 23 || month === 9 && day <= 22) return 5; // Virgo
    if (month === 9 && signChange(month, day, 23, 22)) return 6; // Libra
    if (month === 9 && day >= 23 || month === 10 && day <= 22) return 6; // Libra
    if (month === 10 && day >= 23 || month === 11 && day <= 21) return 7; // Scorpio
    if (month === 11 && day >= 22 || month === 12 && day <= 21) return 8; // Sagittarius
    if (month === 12 && day >= 22 || month === 1 && day <= 19) return 9; // Capricorn
    if (month === 1 && day >= 20 || month === 2 && day <= 18) return 10; // Aquarius
    return 11; // Pisces
  };
  const signChange = (m: number, d: number, start: number, end: number) => true;
  const generateChartData = () => {
    // In a real app, this would call an API like swisseph or astrologyapi.com
    // Here we generate deterministic pseudo-random data based on the date string
    const seed = formData.date.split('-').join('') + formData.time.replace(':', '');
    const numSeed = parseInt(seed) || 123456;
    // Calculate Ascendant (Asc) based roughly on time
    const timeParts = formData.time.split(':');
    const hours = parseInt(timeParts[0]) || 12;
    const ascDegree = (hours * 15 + numSeed % 30) % 360;
    // Sun is based on date
    const sunSignIdx = getZodiacSign(formData.date);
    const sunDegree = sunSignIdx * 30 + numSeed % 30;
    // Generate other planets pseudo-randomly but deterministically
    const positions = PLANETS.map((planet, i) => {
      let degree;
      if (planet.id === 'sun') {
        degree = sunDegree;
      } else {
        degree = numSeed * (i + 1) * 17 % 360;
      }
      const signIdx = Math.floor(degree / 30);
      const degreeInSign = Math.floor(degree % 30);
      const minutes = Math.floor(numSeed * (i + 7) % 60);
      return {
        ...planet,
        absDegree: degree,
        signIdx,
        sign: ZODIAC_SIGNS[signIdx],
        formatted: `${degreeInSign}° ${minutes}'`,
        house: Math.floor((degree - ascDegree + 360) % 360 / 30) + 1
      };
    });
    // Generate aspects (lines between planets)
    const aspects = [];
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const diff = Math.abs(positions[i].absDegree - positions[j].absDegree);
        const shortestDiff = Math.min(diff, 360 - diff);
        // Check for major aspects (Conjunction 0, Sextile 60, Square 90, Trine 120, Opposition 180)
        // with a 5 degree orb
        if (Math.abs(shortestDiff - 120) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'trine',
            color: '#10b981'
          }); // Green
        } else if (Math.abs(shortestDiff - 90) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'square',
            color: '#ef4444'
          }); // Red
        } else if (Math.abs(shortestDiff - 180) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'opposition',
            color: '#ef4444'
          }); // Red
        } else if (Math.abs(shortestDiff - 60) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'sextile',
            color: '#3b82f6'
          }); // Blue
        }
      }
    }
    // Ensure we have at least some aspects for visual flair
    if (aspects.length < 5) {
      aspects.push({
        p1: 0,
        p2: 1,
        type: 'trine',
        color: '#10b981'
      });
      aspects.push({
        p1: 2,
        p2: 4,
        type: 'square',
        color: '#ef4444'
      });
      aspects.push({
        p1: 3,
        p2: 5,
        type: 'sextile',
        color: '#3b82f6'
      });
    }
    const ascSignIdx = Math.floor(ascDegree / 30);
    setChartData({
      ascendant: ascDegree,
      ascSignIdx: ascSignIdx,
      positions,
      aspects,
      sunSign: ZODIAC_SIGNS[sunSignIdx]
    });
  };
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setIsCalculating(true);
    setProgress(0);
    // Simulate calculation steps
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          return 100;
        }
        return p + 5;
      });
    }, 100);
    setTimeout(() => {
      generateChartData();
      setIsCalculating(false);
      setShowResults(true);
      window.scrollTo(0, 0);
    }, 2500);
  };
  // SVG Chart Component
  const renderNatalChart = () => {
    if (!chartData) return null;
    const size = 500;
    const center = size / 2;
    const outerRadius = 220;
    const innerRadius = 160;
    const coreRadius = 80;
    // The Ascendant is traditionally placed on the left (180 degrees in SVG math)
    // We need to rotate the entire chart so the Ascendant degree is on the left
    const rotationOffset = 180 - chartData.ascendant;
    return <div className="relative w-full max-w-[500px] aspect-square mx-auto bg-slate-900 rounded-full shadow-2xl overflow-hidden border-4 border-slate-800" data-id="element-21080">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full" data-id="element-21081">
          <defs data-id="element-21082">
            <radialGradient id="bg-grad" cx="50%" cy="50%" r="50%" data-id="element-21083">
              <stop offset="0%" stopColor="#1e293b" data-id="element-21084" />
              <stop offset="100%" stopColor="#0f172a" data-id="element-21085" />
            </radialGradient>
          </defs>

          <rect width={size} height={size} fill="url(#bg-grad)" data-id="element-21086" />

          {/* Rotate group based on Ascendant */}
          <g transform={`rotate(${rotationOffset}, ${center}, ${center})`} data-id="element-21087">
            {/* Zodiac Ring */}
            <circle cx={center} cy={center} r={outerRadius} fill="none" stroke="#334155" strokeWidth="1" data-id="element-21088" />
            <circle cx={center} cy={center} r={innerRadius} fill="none" stroke="#334155" strokeWidth="1" data-id="element-21089" />

            {/* Zodiac Signs */}
            {ZODIAC_SIGNS.map((sign, i) => {
            const startAngle = i * 30;
            const endAngle = (i + 1) * 30;
            const midAngle = startAngle + 15;
            // SVG Math for drawing the segment
            const startRad = (startAngle - 90) * (Math.PI / 180);
            const endRad = (endAngle - 90) * (Math.PI / 180);
            const midRad = (midAngle - 90) * (Math.PI / 180);
            const x1 = center + outerRadius * Math.cos(startRad);
            const y1 = center + outerRadius * Math.sin(startRad);
            const x2 = center + innerRadius * Math.cos(startRad);
            const y2 = center + innerRadius * Math.sin(startRad);
            // Text position
            const textR = (outerRadius + innerRadius) / 2;
            const textX = center + textR * Math.cos(midRad);
            const textY = center + textR * Math.sin(midRad);
            return <g key={`sign-${i}`} data-id="element-21090">
                  {/* Divider line */}
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1" data-id="element-21091" />

                  {/* Sign Symbol */}
                  <text x={textX} y={textY} fill={sign.color} fontSize="24" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${midAngle}, ${textX}, ${textY})`} className="font-bold" data-id="element-21092">
                    {sign.symbol}
                  </text>

                  {/* Degree markers (small ticks) */}
                  {[...Array(30)].map((_, tick) => {
                if (tick % 5 !== 0) return null;
                const tickAngle = startAngle + tick;
                const tickRad = (tickAngle - 90) * (Math.PI / 180);
                const tx1 = center + innerRadius * Math.cos(tickRad);
                const ty1 = center + innerRadius * Math.sin(tickRad);
                const tx2 = center + (innerRadius + 5) * Math.cos(tickRad);
                const ty2 = center + (innerRadius + 5) * Math.sin(tickRad);
                return <line key={`tick-${i}-${tick}`} x1={tx1} y1={ty1} x2={tx2} y2={ty2} stroke="#475569" strokeWidth="1" data-id="element-21093" />;
              })}
                </g>;
          })}

            {/* Houses (Simplified, equal house system for demo) */}
            <circle cx={center} cy={center} r={coreRadius} fill="none" stroke="#334155" strokeWidth="1" data-id="element-21094" />
            {[...Array(12)].map((_, i) => {
            const angle = chartData.ascendant + i * 30;
            const rad = (angle - 90) * (Math.PI / 180);
            const x1 = center + innerRadius * Math.cos(rad);
            const y1 = center + innerRadius * Math.sin(rad);
            const x2 = center + coreRadius * Math.cos(rad);
            const y2 = center + coreRadius * Math.sin(rad);
            // House number
            const midAngle = angle + 15;
            const midRad = (midAngle - 90) * (Math.PI / 180);
            const textR = innerRadius - 20;
            const textX = center + textR * Math.cos(midRad);
            const textY = center + textR * Math.sin(midRad);
            // Ascendant/Descendant/MC/IC lines are thicker
            const isAngle = i % 3 === 0;
            return <g key={`house-${i}`} data-id="element-21095">
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={isAngle ? '#cbd5e1' : '#475569'} strokeWidth={isAngle ? '2' : '1'} data-id="element-21096" />
                  <text x={textX} y={textY} fill="#64748b" fontSize="12" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${midAngle - rotationOffset}, ${textX}, ${textY})`} // Keep numbers upright relative to screen
              data-id="element-21097">
                    {i + 1}
                  </text>
                </g>;
          })}

            {/* Aspect Lines (Center) */}
            {chartData.aspects.map((aspect: any, i: number) => {
            const p1 = chartData.positions[aspect.p1];
            const p2 = chartData.positions[aspect.p2];
            const rad1 = (p1.absDegree - 90) * (Math.PI / 180);
            const rad2 = (p2.absDegree - 90) * (Math.PI / 180);
            const x1 = center + coreRadius * Math.cos(rad1);
            const y1 = center + coreRadius * Math.sin(rad1);
            const x2 = center + coreRadius * Math.cos(rad2);
            const y2 = center + coreRadius * Math.sin(rad2);
            return <line key={`aspect-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={aspect.color} strokeWidth="1.5" opacity="0.6" data-id="element-21098" />;
          })}

            {/* Planets */}
            {chartData.positions.map((planet: any, i: number) => {
            const rad = (planet.absDegree - 90) * (Math.PI / 180);
            // Stagger radius slightly to avoid overlaps
            const r = innerRadius - 40 + i % 3 * 12;
            const x = center + r * Math.cos(rad);
            const y = center + r * Math.sin(rad);
            // Line from planet to outer edge
            const lx1 = center + innerRadius * Math.cos(rad);
            const ly1 = center + innerRadius * Math.sin(rad);
            const lx2 = center + (innerRadius - 10) * Math.cos(rad);
            const ly2 = center + (innerRadius - 10) * Math.sin(rad);
            return <g key={`planet-${i}`} data-id="element-21099">
                  <line x1={lx1} y1={ly1} x2={lx2} y2={ly2} stroke={planet.color} strokeWidth="1" data-id="element-21100" />
                  <circle cx={x} cy={y} r="12" fill="#1e293b" stroke={planet.color} strokeWidth="1" data-id="element-21101" />
                  {/* We use text for planet symbols in SVG to keep it simple */}
                  <text x={x} y={y} fill={planet.color} fontSize="14" textAnchor="middle" dominantBaseline="central" transform={`rotate(${-rotationOffset}, ${x}, ${y})`} // Keep upright
              data-id="element-21102">
                    {planet.id === 'sun' ? '☉' : planet.id === 'moon' ? '☽' : planet.id === 'mercury' ? '☿' : planet.id === 'venus' ? '♀' : planet.id === 'mars' ? '♂' : planet.id === 'jupiter' ? '♃' : planet.id === 'saturn' ? '♄' : planet.id === 'uranus' ? '♅' : planet.id === 'neptune' ? '♆' : '♇'}
                  </text>
                </g>;
          })}
          </g>

          {/* Center decorative element */}
          <circle cx={center} cy={center} r="15" fill="#0f172a" stroke="#475569" strokeWidth="1" data-id="element-21103" />
          <circle cx={center} cy={center} r="3" fill="#cbd5e1" data-id="element-21104" />

          {/* Ascendant / Descendant labels (Fixed to screen) */}
          <text x="20" y={center} fill="#cbd5e1" fontSize="12" fontWeight="bold" dominantBaseline="middle" data-id="element-21105">
            ASC
          </text>
          <text x={size - 40} y={center} fill="#cbd5e1" fontSize="12" fontWeight="bold" dominantBaseline="middle" data-id="element-21106">
            DSC
          </text>
          <text x={center} y="20" fill="#cbd5e1" fontSize="12" fontWeight="bold" textAnchor="middle" data-id="element-21107">
            MC
          </text>
          <text x={center} y={size - 10} fill="#cbd5e1" fontSize="12" fontWeight="bold" textAnchor="middle" data-id="element-21108">
            IC
          </text>
        </svg>
      </div>;
  };
  if (showResults && chartData) {
    return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-21109">
        {/* Header */}
        <div className="bg-slate-900 text-white pt-12 pb-20 border-b border-slate-800" data-id="element-21110">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21111">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-id="element-21112">
              <div data-id="element-21113">
                <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4" data-id="element-21114">
                  Интерактивная карта
                </Badge>
                <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-21115">
                  Натальная карта: {formData.name}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400" data-id="element-21116">
                  <span className="flex items-center" data-id="element-21117">
                    <Calendar className="w-4 h-4 mr-1.5" data-id="element-21118" /> {formData.date}
                  </span>
                  <span className="flex items-center" data-id="element-21119">
                    <Clock className="w-4 h-4 mr-1.5" data-id="element-21120" /> {formData.time}
                  </span>
                  <span className="flex items-center" data-id="element-21121">
                    <MapPin className="w-4 h-4 mr-1.5" data-id="element-21122" /> {formData.city}
                  </span>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-21123">
                <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => setShowResults(false)} data-id="element-21124">
                  Новый расчёт
                </Button>
                <Button variant="primary" icon={<Download className="w-4 h-4" data-id="element-21126" />} data-id="element-21125">
                  Скачать PDF
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10" data-id="element-21127">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-21128">
            {/* Left Column: Chart Visualization */}
            <div className="lg:col-span-7" data-id="element-21129">
              <Card padding="lg" className="bg-white shadow-xl border-slate-200" data-id="element-21130">
                <div className="flex justify-between items-center mb-8" data-id="element-21131">
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-21132">
                    Космограмма
                  </h3>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-500" data-id="element-21133">
                    <span data-id="element-21134">ASC: {Math.floor(chartData.ascendant)}°</span>
                  </div>
                </div>

                {renderNatalChart()}

                <div className="mt-8 flex flex-wrap justify-center gap-4 text-xs text-slate-500" data-id="element-21135">
                  <div className="flex items-center gap-1.5" data-id="element-21136">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full" data-id="element-21137"></div>{' '}
                    Трин (120°)
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-21138">
                    <div className="w-3 h-3 bg-blue-500 rounded-full" data-id="element-21139"></div>{' '}
                    Секстиль (60°)
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-21140">
                    <div className="w-3 h-3 bg-red-500 rounded-full" data-id="element-21141"></div>{' '}
                    Квадрат (90°) / Оппозиция (180°)
                  </div>
                </div>
              </Card>

              {/* Brief Interpretation */}
              <Card padding="lg" className="mt-8 border-indigo-100 bg-indigo-50/30" data-id="element-21142">
                <div className="flex items-start gap-4" data-id="element-21143">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-2xl flex-shrink-0" data-id="element-21144">
                    {chartData.sunSign.symbol}
                  </div>
                  <div data-id="element-21145">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-21146">
                      Ваше Солнце в знаке {chartData.sunSign.name}
                    </h3>
                    <p className="text-slate-700 text-sm leading-relaxed mb-4" data-id="element-21147">
                      {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].text}
                    </p>
                    <div className="bg-white p-4 rounded-xl border border-indigo-100 text-sm" data-id="element-21148">
                      <div className="font-bold text-slate-900 mb-1" data-id="element-21149">
                        Ключевые аспекты:
                      </div>
                      <ul className="list-disc list-inside text-slate-600 space-y-1" data-id="element-21150">
                        <li data-id="element-21151">
                          Асцендент в {ZODIAC_SIGNS[chartData.ascSignIdx].name}{' '}
                          определяет то, как вас видят другие.
                        </li>
                        <li data-id="element-21152">
                          Луна в {chartData.positions[1].sign.name} влияет на
                          ваши эмоции и подсознание.
                        </li>
                        <li data-id="element-21153">
                          Найдено {chartData.aspects.length} мажорных аспектов
                          между планетами.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column: Data Tables */}
            <div className="lg:col-span-5 space-y-8" data-id="element-21154">
              <Card padding="none" className="overflow-hidden shadow-lg" data-id="element-21155">
                <div className="p-5 border-b border-slate-100 bg-slate-50/80 flex justify-between items-center" data-id="element-21156">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-21157">
                    <Star className="w-5 h-5 text-indigo-500" data-id="element-21158" /> Положения
                    планет
                  </h3>
                </div>
                <div className="overflow-x-auto" data-id="element-21159">
                  <table className="w-full text-left text-sm" data-id="element-21160">
                    <thead className="bg-white text-slate-400 border-b border-slate-100" data-id="element-21161">
                      <tr data-id="element-21162">
                        <th className="px-5 py-3 font-medium" data-id="element-21163">Планета</th>
                        <th className="px-5 py-3 font-medium" data-id="element-21164">Знак</th>
                        <th className="px-5 py-3 font-medium" data-id="element-21165">Дом</th>
                        <th className="px-5 py-3 font-medium text-right" data-id="element-21166">
                          Градус
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50" data-id="element-21167">
                      {chartData.positions.map((planet: any, i: number) => <tr key={i} className="hover:bg-slate-50 transition-colors" data-id="element-21168">
                          <td className="px-5 py-3" data-id="element-21169">
                            <div className="flex items-center gap-2 font-medium text-slate-900" data-id="element-21170">
                              <span style={{
                            color: planet.color
                          }} data-id="element-21171">
                                {planet.icon}
                              </span>
                              {planet.name}
                            </div>
                          </td>
                          <td className="px-5 py-3" data-id="element-21172">
                            <div className="flex items-center gap-1.5 text-slate-700" data-id="element-21173">
                              <span style={{
                            color: planet.sign.color
                          }} className="font-bold" data-id="element-21174">
                                {planet.sign.symbol}
                              </span>
                              {planet.sign.name}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-slate-600 font-medium text-center" data-id="element-21175">
                            {planet.house}
                          </td>
                          <td className="px-5 py-3 text-slate-500 text-right font-mono text-xs" data-id="element-21176">
                            {planet.formatted}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Full Interpretations Section */}
              <div className="mt-16 mb-12" data-id="element-21177">
                <div className="text-center mb-10" data-id="element-21178">
                  <Badge variant="pro" className="mb-4 bg-indigo-100 text-indigo-700 border-indigo-200" data-id="element-21179">
                    <Sparkles className="w-3 h-3 mr-1.5" data-id="element-21180" /> Детальный анализ
                  </Badge>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-21181">
                    Подробный разбор вашей карты
                  </h2>
                  <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-21182">
                    Изучите влияние каждой планеты на ваш характер, таланты и
                    жизненный путь.
                  </p>
                </div>

                <div className="space-y-8" data-id="element-21183">
                  {/* Core Personality */}
                  <Card padding="lg" className="border-slate-200 shadow-md" data-id="element-21184">
                    <div className="flex items-center gap-3 mb-6" data-id="element-21185">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-21186">
                        <Sun className="w-5 h-5 text-amber-600" data-id="element-21187" />
                      </div>
                      <div data-id="element-21188">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-21189">
                          Ядро личности
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-21190">
                          Основа вашего характера, эмоций и социального
                          проявления
                        </p>
                      </div>
                    </div>
                    <Accordion items={[{
                    id: 'sun',
                    title: `Солнце в знаке ${chartData.positions[0].sign.name}`,
                    content: <div className="space-y-4 pt-2" data-id="element-21192">
                              <p className="text-slate-700" data-id="element-21193">
                                {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21194">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21195">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21196">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21197" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21198">
                                    {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21199">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21200">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21201">
                                    <Zap className="w-4 h-4" data-id="element-21202" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21203">
                                    {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21204">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'moon',
                    title: `Луна в знаке ${chartData.positions[1].sign.name}`,
                    content: <div className="space-y-4 pt-2" data-id="element-21205">
                              <p className="text-slate-700" data-id="element-21206">
                                {PLANET_INTERPRETATIONS.moon[chartData.positions[1].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21207">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21208">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21209">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21210" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21211">
                                    {PLANET_INTERPRETATIONS.moon[chartData.positions[1].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21212">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21213">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21214">
                                    <Zap className="w-4 h-4" data-id="element-21215" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21216">
                                    {PLANET_INTERPRETATIONS.moon[chartData.positions[1].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21217">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'ascendant',
                    title: ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].title,
                    content: <div className="space-y-4 pt-2" data-id="element-21218">
                              <p className="text-slate-700" data-id="element-21219">
                                {ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21220">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21221">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21222">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21223" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21224">
                                    {ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].strengths.map((s, i) => <li key={i} data-id="element-21225">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21226">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21227">
                                    <Zap className="w-4 h-4" data-id="element-21228" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21229">
                                    {ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].challenges.map((c, i) => <li key={i} data-id="element-21230">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }]} data-id="element-21191" />
                  </Card>

                  {/* Personal Planets */}
                  <Card padding="lg" className="border-slate-200 shadow-md" data-id="element-21231">
                    <div className="flex items-center gap-3 mb-6" data-id="element-21232">
                      <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-21233">
                        <Star className="w-5 h-5 text-rose-600" data-id="element-21234" />
                      </div>
                      <div data-id="element-21235">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-21236">
                          Личные качества
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-21237">
                          Мышление, любовь, ценности и способы действия
                        </p>
                      </div>
                    </div>
                    <Accordion items={[{
                    id: 'mercury',
                    title: `Меркурий в знаке ${chartData.positions[2].sign.name} (Мышление и общение)`,
                    content: <div className="space-y-4 pt-2" data-id="element-21239">
                              <p className="text-slate-700" data-id="element-21240">
                                {PLANET_INTERPRETATIONS.mercury[chartData.positions[2].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21241">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21242">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21243">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21244" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21245">
                                    {PLANET_INTERPRETATIONS.mercury[chartData.positions[2].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21246">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21247">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21248">
                                    <Zap className="w-4 h-4" data-id="element-21249" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21250">
                                    {PLANET_INTERPRETATIONS.mercury[chartData.positions[2].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21251">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'venus',
                    title: `Венера в знаке ${chartData.positions[3].sign.name} (Любовь и ценности)`,
                    content: <div className="space-y-4 pt-2" data-id="element-21252">
                              <p className="text-slate-700" data-id="element-21253">
                                {PLANET_INTERPRETATIONS.venus[chartData.positions[3].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21254">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21255">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21256">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21257" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21258">
                                    {PLANET_INTERPRETATIONS.venus[chartData.positions[3].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21259">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21260">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21261">
                                    <Zap className="w-4 h-4" data-id="element-21262" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21263">
                                    {PLANET_INTERPRETATIONS.venus[chartData.positions[3].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21264">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'mars',
                    title: `Марс в знаке ${chartData.positions[4].sign.name} (Энергия и действия)`,
                    content: <div className="space-y-4 pt-2" data-id="element-21265">
                              <p className="text-slate-700" data-id="element-21266">
                                {PLANET_INTERPRETATIONS.mars[chartData.positions[4].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21267">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21268">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21269">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21270" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21271">
                                    {PLANET_INTERPRETATIONS.mars[chartData.positions[4].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21272">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21273">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21274">
                                    <Zap className="w-4 h-4" data-id="element-21275" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21276">
                                    {PLANET_INTERPRETATIONS.mars[chartData.positions[4].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21277">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }]} data-id="element-21238" />
                  </Card>

                  {/* Social and Outer Planets */}
                  <Card padding="lg" className="border-slate-200 shadow-md" data-id="element-21278">
                    <div className="flex items-center gap-3 mb-6" data-id="element-21279">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-21280">
                        <Star className="w-5 h-5 text-indigo-600" data-id="element-21281" />
                      </div>
                      <div data-id="element-21282">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-21283">
                          Социальные и высшие планеты
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-21284">
                          Взаимодействие с обществом и глобальные трансформации
                        </p>
                      </div>
                    </div>
                    <Accordion items={[5, 6, 7, 8, 9].map(idx => {
                    const planet = chartData.positions[idx];
                    const interp = PLANET_INTERPRETATIONS[planet.id][planet.signIdx];
                    return {
                      id: planet.id,
                      title: interp.title,
                      content: <div className="space-y-4 pt-2" data-id="element-21286">
                              <p className="text-slate-700" data-id="element-21287">{interp.text}</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21288">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21289">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21290">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21291" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21292">
                                    {interp.strengths.map((s: string, i: number) => <li key={i} data-id="element-21293">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21294">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21295">
                                    <Zap className="w-4 h-4" data-id="element-21296" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21297">
                                    {interp.challenges.map((c: string, i: number) => <li key={i} data-id="element-21298">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                    };
                  })} data-id="element-21285" />
                  </Card>
                </div>
              </div>

              {/* Upsell to Full Report */}
              <Card padding="lg" className="bg-gradient-to-br from-slate-900 to-indigo-900 text-white border-none shadow-xl relative overflow-hidden" data-id="element-21299">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full blur-3xl opacity-30" data-id="element-21300"></div>
                <div className="relative z-10" data-id="element-21301">
                  <h3 className="text-xl font-bold mb-3" data-id="element-21302">
                    Нужна полная расшифровка?
                  </h3>
                  <p className="text-indigo-100 text-sm mb-6 leading-relaxed" data-id="element-21303">
                    Это лишь базовые данные. Закажите полный авто-отчёт (30+
                    страниц), чтобы узнать, как эти положения планет влияют на
                    ваш характер, карьеру, финансы и отношения.
                  </p>
                  <Button variant="primary" fullWidth className="bg-white text-indigo-900 hover:bg-slate-100" onClick={() => onNavigate('service-natal')} data-id="element-21304">
                    Получить полный разбор за 3 900 ₽
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>;
  }
  // Form View
  return <div className="w-full bg-slate-50 min-h-screen pb-24 relative overflow-hidden" data-id="element-21305">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-indigo-900 to-slate-50 z-0" data-id="element-21306"></div>
      <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500 rounded-full blur-[100px] opacity-20 z-0" data-id="element-21307"></div>
      <div className="absolute top-40 right-10 w-80 h-80 bg-blue-500 rounded-full blur-[100px] opacity-20 z-0" data-id="element-21308"></div>

      <div className="relative z-10 pt-16 pb-16" data-id="element-21309">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21310">
          <div className="text-center mb-12 text-white" data-id="element-21311">
            <Badge variant="pro" className="bg-white/20 text-white border-white/30 mb-6 backdrop-blur-sm" data-id="element-21312">
              <Sparkles className="w-3 h-3 mr-1.5" data-id="element-21313" /> Онлайн генератор
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-md" data-id="element-21314">
              Постройте свою натальную карту
            </h1>
            <p className="text-xl text-indigo-100 max-w-2xl mx-auto drop-shadow" data-id="element-21315">
              Введите данные рождения, чтобы мгновенно получить точный расчёт
              положений планет и интерактивную космограмму.
            </p>
          </div>

          <Card padding="lg" className="shadow-2xl border-0 ring-1 ring-slate-200/50 bg-white/95 backdrop-blur-xl" data-id="element-21316">
            {isCalculating ? <div className="py-16 flex flex-col items-center justify-center text-center" data-id="element-21317">
                <div className="relative w-24 h-24 mb-8" data-id="element-21318">
                  <svg className="animate-spin w-full h-full text-indigo-200" viewBox="0 0 24 24" data-id="element-21319">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-21320"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-21321"></path>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-21322">
                    <Star className="w-8 h-8 text-indigo-600 animate-pulse" data-id="element-21323" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-21324">
                  Рассчитываем эфемериды...
                </h3>
                <p className="text-slate-500 mb-8 max-w-sm" data-id="element-21325">
                  Вычисляем точные координаты планет, домов и аспектов на момент
                  вашего рождения
                </p>

                <div className="w-full max-w-md bg-slate-100 rounded-full h-2.5 mb-2 overflow-hidden" data-id="element-21326">
                  <div className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-21327"></div>
                </div>
                <div className="text-sm font-medium text-indigo-600" data-id="element-21328">
                  {progress}%
                </div>
              </div> : <form onSubmit={handleCalculate} className="space-y-8" data-id="element-21329">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-21330">
                  <div className="space-y-6" data-id="element-21331">
                    <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2" data-id="element-21332">
                      Личные данные
                    </h3>
                    <Input label="Ваше имя" placeholder="Например, Алексей" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} data-id="element-21333" />
                    <Input label="Город рождения" placeholder="Например, Москва" required icon={<MapPin className="w-4 h-4" data-id="element-21335" />} value={formData.city} onChange={e => setFormData({
                  ...formData,
                  city: e.target.value
                })} data-id="element-21334" />
                  </div>

                  <div className="space-y-6" data-id="element-21336">
                    <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2" data-id="element-21337">
                      Время рождения
                    </h3>
                    <Input label="Дата рождения" type="date" required value={formData.date} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-21338" />
                    <div data-id="element-21339">
                      <Input label="Время рождения" type="time" required value={formData.time} onChange={e => setFormData({
                    ...formData,
                    time: e.target.value
                  })} data-id="element-21340" />
                      <p className="text-xs text-slate-500 mt-1.5 flex items-start gap-1" data-id="element-21341">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-21342" />
                        Точное время необходимо для расчета сетки домов и
                        Асцендента
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-21343">
                  <div className="text-sm text-slate-500 flex items-center gap-2" data-id="element-21344">
                    <Lock className="w-4 h-4 text-slate-400" data-id="element-21345" />
                    Данные не сохраняются и используются только для расчёта
                  </div>
                  <Button type="submit" variant="primary" size="lg" className="w-full sm:w-auto px-8" icon={<Sparkles className="w-5 h-5" data-id="element-21347" />} data-id="element-21346">
                    Построить карту
                  </Button>
                </div>
              </form>}
          </Card>

          {/* Features below form */}
          {!isCalculating && <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" data-id="element-21348">
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 text-center" data-id="element-21349">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-21350">
                  <RefreshCw className="w-6 h-6" data-id="element-21351" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2" data-id="element-21352">
                  Швейцарские эфемериды
                </h4>
                <p className="text-sm text-slate-600" data-id="element-21353">
                  Высочайшая точность астрономических расчетов положений планет.
                </p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 text-center" data-id="element-21354">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-21355">
                  <CircleDot className="w-6 h-6" data-id="element-21356" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2" data-id="element-21357">
                  Интерактивная карта
                </h4>
                <p className="text-sm text-slate-600" data-id="element-21358">
                  Наглядная визуализация аспектов, домов и планет в знаках
                  зодиака.
                </p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 text-center" data-id="element-21359">
                <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-21360">
                  <FileText className="w-6 h-6" data-id="element-21361" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2" data-id="element-21362">
                  Базовая расшифровка
                </h4>
                <p className="text-sm text-slate-600" data-id="element-21363">
                  Узнайте свой солнечный знак, асцендент и доминирующие стихии.
                </p>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}